import type { AppEndpointBuilder } from 'store/types';
import type { Paginate, BasePaginationParameters } from 'types';

import {
  toBankDetailsModel,
  toEarningsSummaryModel,
  toPayoutRequestModel,
  toReferralsDetailsModel,
  toReferralTransactionModel,
  toReferredUserModel,
} from './helpers';
import type {
  ReferralsDetailsModel,
  BankDetailsModel,
  ReferredUserModel,
  ReferralTransactionModel,
  PayoutRequestModel,
  EarningsSummaryModel,
} from './models';
import type { PayoutPayload, PayoutType, UpsertBankDetailsPayload } from './payloads';
import type { EarningsPeriod } from './types';

export const referralsEndpoints = (builder: AppEndpointBuilder) => ({
  // #region Queries
  getBankDetails: builder.query<BankDetailsModel, void>({
    query: () => ({
      url: 'referrals/bank-details',
      method: 'GET',
    }),
    providesTags: ['Referrals-Bank-Details'],
    transformResponse: toBankDetailsModel,
  }),

  getReferralsDetails: builder.query<ReferralsDetailsModel, void>({
    query: () => ({
      url: 'referrals/details',
      method: 'GET',
    }),
    providesTags: ['Referrals-Details'],
    transformResponse: toReferralsDetailsModel,
  }),

  getPayoutDetails: builder.query<ReferralsDetailsModel, PayoutType>({
    query: (type) => ({
      url: `referrals/details/${type}`,
      method: 'GET',
    }),
    providesTags: (_, __, type) => [{ type: 'Referrals-Details', id: type }],
    transformResponse: toReferralsDetailsModel,
  }),

  getReferralEarningsSummary: builder.query<EarningsSummaryModel, EarningsPeriod>({
    query: (period) => ({
      url: `referrals/earnings/${period}`,
      method: 'GET',
    }),
    providesTags: (_, __, period) => [{ type: 'Referrals-Earnings-Stats', id: period }],
    transformResponse: toEarningsSummaryModel,
  }),

  getReferralPayoutRequests: builder.query<Paginate<PayoutRequestModel>, BasePaginationParameters>({
    query: (params) => ({
      url: 'referrals/payout-requests',
      method: 'GET',
      params,
    }),
    providesTags: (result) =>
      result?.data
        ? [
            ...result.data.map(({ createdAt }) => ({ type: 'Referrals-Payout-Request' as const, id: createdAt })),
            { type: 'Referrals-Payout-Request', id: 'LIST' },
          ]
        : [{ type: 'Referrals-Payout-Request', id: 'LIST' }],
    transformResponse: toPayoutRequestModel,
  }),

  getReferralTransactions: builder.query<Paginate<ReferralTransactionModel>, BasePaginationParameters>({
    query: (params) => ({
      url: 'referrals/transactions',
      method: 'GET',
      params,
    }),
    providesTags: (result) =>
      result?.data
        ? [
            ...result.data.map(({ appliedAt }) => ({ type: 'Referrals-Transaction' as const, id: appliedAt })),
            { type: 'Referrals-Transaction', id: 'LIST' },
          ]
        : [{ type: 'Referrals-Transaction', id: 'LIST' }],
    transformResponse: toReferralTransactionModel,
  }),

  getReferredUsers: builder.query<Paginate<ReferredUserModel>, BasePaginationParameters>({
    query: (params) => ({
      url: 'referrals/referred-users',
      method: 'GET',
      params,
    }),
    providesTags: (result) =>
      result?.data
        ? [
            ...result.data.map(({ email }) => ({ type: 'Referrals-User' as const, id: email })),
            { type: 'Referrals-User', id: 'LIST' },
          ]
        : [{ type: 'Referrals-User', id: 'LIST' }],
    transformResponse: toReferredUserModel,
  }),
  // #endregion Queries

  // #region Mutations
  upsertBankDetails: builder.mutation<void, UpsertBankDetailsPayload>({
    query: (data) => ({
      url: 'referrals/bank-details',
      method: 'POST',
      data,
    }),
    invalidatesTags: ['Referrals-Bank-Details'],
  }),

  referralsPayout: builder.mutation<void, PayoutPayload>({
    query: (data) => ({
      url: 'referrals/payout',
      method: 'POST',
      data,
    }),
    invalidatesTags: ['Referrals-Details'],
  }),
  // #endregion Mutations
});
